// LotteryScene.js - 抽奖界面（横版老虎机风格）
import LotterySystem from './modules/LotterySystem.js';
import DebugHelper from './modules/DebugHelper.js';
import GameStateStore from './modules/GameStateStore.js';
import UIStyleHelper from './modules/UIStyleHelper.js';
import BackgroundHelper from './modules/BackgroundHelper.js';
import { playSceneSfx } from './modules/LazyAssetLoader.js';

const FONT = UIStyleHelper.PIXEL_FONT;

const RARITY = {
    common: { label: '普通', color: '#aaaaaa', bg: 0x2a2a3e },
    rare: { label: '稀有', color: '#4cc9f0', bg: 0x1a3048 },
    epic: { label: '史诗', color: '#c77dff', bg: 0x301a48 },
    legendary: { label: '传说', color: '#ffd700', bg: 0x3a3010 },
    jackpot: { label: '大奖', color: '#ff6b6b', bg: 0x481818 }
};

export default class LotteryScene extends Phaser.Scene {
    constructor() {
        super('LotteryScene');
    }

    init(data) {
        this.previousSceneKey = data?.previousSceneKey || 'WorldScene';
        this.ticketType = data?.ticketType || 'lottery_ticket_tier1';
        this.playerData = GameStateStore.getPlayer();
        this.winningPrize = null;
        this.isJackpot = false;
        this.phase = 'intro';
        this.contentLayer = null;
        this.slotContainer = null;
        this.highlightGfx = null;
    }

    create() {
        this.scene.bringToTop('LotteryScene');
        BackgroundHelper.createSemiTransparentBackground(this, 0.92);
        this.contentLayer = this.add.container(0, 0).setDepth(10);

        if (!LotterySystem.hasTicket(this.playerData, this.ticketType)) {
            this.showSimpleMessage('没有抽奖券！', '#ff6666', 1800);
            return;
        }

        const preview = this.rollPrize();
        if (!preview.success) {
            this.showSimpleMessage(preview.message, '#ff6666', 1800);
            return;
        }

        this.winningPrize = preview.prize;
        this.isJackpot = preview.isJackpot;
        this.showIntro();
    }

    rollPrize() {
        if (Math.random() * 100 < LotterySystem.LOTTERY_CONFIG.jackpot.chance) {
            return {
                success: true,
                prize: LotterySystem.selectJackpotPrize(),
                isJackpot: true
            };
        }
        return {
            success: true,
            prize: LotterySystem.selectPrize(),
            isJackpot: false
        };
    }

    getPrizeRarity(prize, isJackpot = false) {
        if (isJackpot) return RARITY.jackpot;
        const id = prize?.id || '';
        if (id.includes('tier3') || id === 'legendary_fragment' || id === 'golden_lottery_ticket') {
            return RARITY.legendary;
        }
        if (id.includes('tier2') || id.includes('random_equipment') || id === 'lottery_ticket_tier2') {
            return RARITY.epic;
        }
        if (id.includes('fragment') || id.includes('repair') || id.includes('corrosion')) {
            return RARITY.rare;
        }
        return RARITY.common;
    }

    showIntro() {
        this.clearContent();
        this.phase = 'intro';
        const { centerX, centerY } = this.cameras.main;

        const panel = this.add.graphics();
        panel.fillStyle(0x101028, 0.95);
        panel.fillRoundedRect(centerX - 320, centerY - 200, 640, 400, 12);
        panel.lineStyle(3, 0x4cc9f0, 1);
        panel.strokeRoundedRect(centerX - 320, centerY - 200, 640, 400, 12);

        const title = this.add.text(centerX, centerY - 160, '幸运抽奖', {
            fontSize: '32px',
            fill: '#4cc9f0',
            fontFamily: FONT,
            fontStyle: 'bold'
        }).setOrigin(0.5);

        const subtitle = this.add.text(centerX, centerY - 120, '奖品滚动后停在中心格即为所得', {
            fontSize: '14px',
            fill: '#888888',
            fontFamily: FONT
        }).setOrigin(0.5);

        const legendItems = [
            ['普通', RARITY.common.color],
            ['稀有', RARITY.rare.color],
            ['史诗', RARITY.epic.color],
            ['传说', RARITY.legendary.color],
            ['大奖', RARITY.jackpot.color]
        ];
        const legendNodes = legendItems.map(([label, color], i) => {
            const x = centerX - 200 + i * 100;
            return this.add.text(x, centerY - 70, `■ ${label}`, {
                fontSize: '13px',
                fill: color,
                fontFamily: FONT
            }).setOrigin(0.5);
        });

        const pool = this.getDisplayPrizes().slice(0, 6);
        const poolNodes = pool.map((prize, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const x = centerX - 140 + col * 140;
            const y = centerY - 20 + row * 36;
            const rarity = this.getPrizeRarity(prize);
            return this.add.text(x, y, `· ${prize.name}`, {
                fontSize: '13px',
                fill: rarity.color,
                fontFamily: FONT
            }).setOrigin(0, 0.5);
        });

        const hint = this.add.text(centerX, centerY + 150, '按 空格 开始抽奖', {
            fontSize: '18px',
            fill: '#ffff00',
            fontFamily: FONT,
            fontStyle: 'bold'
        }).setOrigin(0.5);

        this.tweens.add({
            targets: hint,
            alpha: { from: 1, to: 0.35 },
            duration: 600,
            yoyo: true,
            repeat: -1
        });

        this.contentLayer.add([panel, title, subtitle, hint, ...legendNodes, ...poolNodes]);

        const start = () => {
            this.input.keyboard.off('keydown-SPACE', start);
            this.startSlotMachine();
        };
        this.input.keyboard.once('keydown-SPACE', start);
        this.input.once('pointerdown', start);
    }

    startSlotMachine() {
        this.clearContent();
        this.phase = 'rolling';
        const { centerX, centerY } = this.cameras.main;
        const itemWidth = 148;
        const itemHeight = 100;
        const gap = 12;

        const title = this.add.text(centerX, 56, '抽奖中...', {
            fontSize: '28px',
            fill: '#4cc9f0',
            fontFamily: FONT,
            fontStyle: 'bold'
        }).setOrigin(0.5);

        const frame = this.add.graphics();
        frame.fillStyle(0x080818, 0.9);
        frame.fillRoundedRect(centerX - 340, centerY - 70, 680, 140, 10);
        frame.lineStyle(2, 0x333355, 1);
        frame.strokeRoundedRect(centerX - 340, centerY - 70, 680, 140, 10);

        this.highlightGfx = this.add.graphics();
        this.highlightGfx.lineStyle(4, 0x00ff88, 1);
        this.highlightGfx.strokeRoundedRect(
            centerX - itemWidth / 2 - 4,
            centerY - itemHeight / 2 - 4,
            itemWidth + 8,
            itemHeight + 8,
            8
        );
        this.highlightGfx.setDepth(30);

        const maskShape = this.make.graphics({ x: centerX - 330, y: centerY - 60 });
        maskShape.fillStyle(0xffffff);
        maskShape.fillRect(0, 0, 660, 120);
        const mask = maskShape.createGeometryMask();

        this.slotContainer = this.add.container(0, centerY);
        this.slotContainer.setMask(mask);

        this.contentLayer.add([title, frame, maskShape, this.highlightGfx, this.slotContainer]);

        const prizes = this.getDisplayPrizes();
        const winningIndex = this.findPrizeIndex(this.winningPrize, prizes);
        const repeat = 8;
        const total = prizes.length * repeat;
        const centerSlot = Math.floor(total / 2);
        let targetSlot = centerSlot;

        for (let offset = 0; offset < prizes.length; offset++) {
            const i1 = centerSlot + offset;
            const i2 = centerSlot - offset;
            if (i1 < total && i1 % prizes.length === winningIndex) {
                targetSlot = i1;
                break;
            }
            if (i2 >= 0 && i2 % prizes.length === winningIndex) {
                targetSlot = i2;
                break;
            }
        }

        const stride = itemWidth + gap;
        this.slotItems = [];

        for (let i = 0; i < total; i++) {
            const prize = prizes[i % prizes.length];
            const x = i * stride;
            const item = this.createSlotItem(prize, x, itemWidth, itemHeight);
            this.slotContainer.add(item);
            this.slotItems.push(item);
        }

        const targetX = centerX - targetSlot * stride;
        const startX = targetX + stride * prizes.length * 3;
        this.slotContainer.x = startX;
        this.slotTargetX = targetX;

        playSceneSfx(this, 'into', { volume: 0.15 });

        this.tweens.add({
            targets: this.slotContainer,
            x: targetX,
            duration: 4500,
            ease: 'Cubic.easeOut',
            onComplete: () => {
                this.slotContainer.x = this.slotTargetX;
                this.onRollComplete();
            }
        });

        this.tweens.add({
            targets: this.highlightGfx,
            alpha: { from: 0.6, to: 1 },
            duration: 300,
            yoyo: true,
            repeat: 14
        });
    }

    createSlotItem(prize, x, w, h) {
        const rarity = this.getPrizeRarity(prize, this.isJackpot && prize.id === this.winningPrize?.id);
        const container = this.add.container(x, 0);

        const bg = this.add.graphics();
        bg.fillStyle(rarity.bg, 0.95);
        bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
        bg.lineStyle(2, Phaser.Display.Color.HexStringToColor(rarity.color).color, 0.8);
        bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);

        const name = this.add.text(0, -12, prize.name || '未知', {
            fontSize: '14px',
            fill: '#ffffff',
            fontFamily: FONT,
            fontStyle: 'bold',
            align: 'center',
            wordWrap: { width: w - 16 }
        }).setOrigin(0.5);

        const badge = this.add.text(0, 22, rarity.label, {
            fontSize: '12px',
            fill: rarity.color,
            fontFamily: FONT
        }).setOrigin(0.5);

        container.add([bg, name, badge]);
        return container;
    }

    onRollComplete() {
        this.phase = 'result';
        const { centerX, centerY } = this.cameras.main;
        const rarity = this.getPrizeRarity(this.winningPrize, this.isJackpot);

        if (this.isJackpot) {
            const itemWidth = 148;
            const itemHeight = 100;
            this.highlightGfx.clear();
            this.highlightGfx.lineStyle(5, 0xffd700, 1);
            this.highlightGfx.strokeRoundedRect(
                centerX - itemWidth / 2 - 4,
                centerY - itemHeight / 2 - 4,
                itemWidth + 8,
                itemHeight + 8,
                8
            );
            this.cameras.main.flash(400, 255, 215, 0);
            this.spawnSparkles(centerX, centerY);
            playSceneSfx(this, 'into', { volume: 0.35 });
        }

        const ticketIndex = this.playerData.inventory.findIndex(
            (item) => item.id === this.ticketType || item.name === '抽奖券'
        );
        if (ticketIndex === -1) {
            this.showSimpleMessage('抽奖券已被使用！', '#ff6666', 1800);
            return;
        }

        this.playerData.inventory.splice(ticketIndex, 1);
        LotterySystem.addPrizeToInventory(this.playerData, this.winningPrize);
        GameStateStore.patchPlayer({ inventory: this.playerData.inventory });
        DebugHelper.log(`LotteryScene: 获得 ${this.winningPrize.name}${this.isJackpot ? ' (大奖)' : ''}`);

        const banner = this.isJackpot ? '★ 大 奖 ★' : '恭喜获得';
        const resultTitle = this.add.text(centerX, centerY + 110, banner, {
            fontSize: this.isJackpot ? '30px' : '24px',
            fill: rarity.color,
            fontFamily: FONT,
            fontStyle: 'bold'
        }).setOrigin(0.5);

        const resultName = this.add.text(centerX, centerY + 148, this.winningPrize.name, {
            fontSize: '22px',
            fill: '#ffffff',
            fontFamily: FONT
        }).setOrigin(0.5);

        const hint = this.add.text(centerX, this.cameras.main.height - 48, '空格 确认领取', {
            fontSize: '15px',
            fill: '#aaaaaa',
            fontFamily: FONT
        }).setOrigin(0.5);

        this.contentLayer.add([resultTitle, resultName, hint]);

        this.tweens.add({
            targets: [resultTitle, resultName],
            scale: { from: 0.8, to: 1 },
            duration: 350,
            ease: 'Back.easeOut'
        });

        const close = () => {
            this.input.keyboard.off('keydown-SPACE', close);
            this.exitScene();
        };
        this.input.keyboard.once('keydown-SPACE', close);
        this.input.once('pointerdown', close);
    }

    spawnSparkles(x, y) {
        for (let i = 0; i < 16; i++) {
            const star = this.add.circle(x, y, 3, 0xffd700);
            const angle = (Math.PI * 2 * i) / 16;
            this.tweens.add({
                targets: star,
                x: x + Math.cos(angle) * (60 + Math.random() * 40),
                y: y + Math.sin(angle) * (40 + Math.random() * 30),
                alpha: 0,
                scale: 0,
                duration: 600 + Math.random() * 300,
                ease: 'Quad.easeOut',
                onComplete: () => star.destroy()
            });
        }
    }

    getDisplayPrizes() {
        const prizes = [];
        const seen = new Set();

        const add = (p) => {
            const key = `${p.id}_${p.name}`;
            if (!seen.has(key)) {
                seen.add(key);
                prizes.push(p);
            }
        };

        LotterySystem.LOTTERY_CONFIG.prizes.forEach(add);
        LotterySystem.LOTTERY_CONFIG.jackpot.prizes.forEach(add);
        return prizes;
    }

    findPrizeIndex(prize, list) {
        for (let i = 0; i < list.length; i++) {
            const p = list[i];
            if (p.id === prize.id) return i;
            if (p.id?.includes('random') && prize.id?.includes('random') && p.type === prize.type && p.tier === prize.tier) {
                return i;
            }
        }
        return 0;
    }

    showSimpleMessage(text, color, delay = 2000) {
        const { centerX, centerY } = this.cameras.main;
        const msg = this.add.text(centerX, centerY, text, {
            fontSize: '24px',
            fill: color,
            fontFamily: FONT
        }).setOrigin(0.5);
        this.contentLayer?.add(msg);
        this.time.delayedCall(delay, () => this.exitScene());
        this.input.keyboard.once('keydown-ESC', () => this.exitScene());
    }

    clearContent() {
        if (this.contentLayer) {
            this.contentLayer.removeAll(true);
        }
        this.slotContainer = null;
        this.highlightGfx = null;
    }

    exitScene() {
        const previousScene = this.scene.get(this.previousSceneKey);
        if (previousScene?.input) {
            previousScene.input.enabled = true;
        }
        this.scene.stop();
        this.scene.resume(this.previousSceneKey);
    }
}
